import { User } from '@models/user.model';
import { Base, PageObject } from '@testsigmainc/models';
import { ApplicationType, Platform } from '@testsigmainc/constants';
import {
  alias,
  custom,
  deserialize,
  serializable,
  serialize,
  SKIP,
} from 'serializr';
import { ApplicationPathType } from '../enums/application-path-type.enum';
import { ExecutionLabType } from '../enums/execution-lab-type.enum';
import { Capability } from './capability.model';
import { ExecutionEnvironment } from './execution-environment.model';

export class MobileInspectionSavedConfiguration
  extends Base
  implements PageObject
{
  @serializable(
    custom(
      (v) => v,
      (v) => v,
    ),
  )
  public applicationType: ApplicationType;
  @serializable
  public id: number;
  @serializable(alias('configName'))
  public name: string;
  @serializable
  public executionLabType: ExecutionLabType;
  @serializable
  public platformOsVersionId: number; // OsVersion
  @serializable
  public platformDeviceId: number; //  Platform
  @serializable
  public environmentId: string;
  @serializable(
    alias(
      'appPathType',
      custom(
        (v) => {
          if (v == ApplicationPathType.USE_PATH) return '1';
          if (v == ApplicationPathType.APP_DETAILS) return '2';
          if (v == ApplicationPathType.UPLOADS) return '3';
        },
        (v) => {
          if (v == '1' || v == 'USE_PATH') return ApplicationPathType.USE_PATH;
          if (v == '2' || v == 'APP_DETAILS')
            return ApplicationPathType.APP_DETAILS;
          if (v == '3' || v == 'UPLOADS') return ApplicationPathType.UPLOADS;
          return v;
        },
      ),
    ),
  )
  public appPathType: ApplicationPathType;
  @serializable
  public appUploadId: number;
  @serializable
  public appUploadVersionId: number;
  @serializable
  public browserStackAppId: string;
  @serializable
  public sauceLabAppId: string;
  @serializable
  public androidAppPackage: string;
  @serializable
  public androidAppActivity: string;
  @serializable
  public iosBundleId: number;
  @serializable(
    custom(
      (v) => {
        if (v == null) return v;
        const capabilities = [];
        v.forEach((item) => capabilities.push(item.serialize()));
        const stringifiedCapabilities = JSON.stringify(capabilities);
        try {
          // Check if the stringified capabilities are valid JSON
          JSON.parse(stringifiedCapabilities);

          return stringifiedCapabilities;
        } catch (e) {
          throw new Error(
            'Desired Capabilities contain Invalid JSON, Please correct and try again!',
          );
        }
      },
      (v) => {
        if (v == null) return v;
        const capabilities = [];

        try {
          if (typeof v == 'string') {
            if (/\\\\"/g.test(v)) {
              v = v.replace(/\\"/g, '"');
              v = v.replace(/\\"/g, '"');
            } else if (v.startsWith('[{\\"')) {
              v = v.replace(/\\"/g, '"');
            }
            v = JSON.parse(v);
            v.forEach((capability) => {
              capabilities.push(new Capability().deserialize(capability));
            });
          } else if (v instanceof Object)
            v.forEach((capability) => {
              capabilities.push(new Capability().deserialize(capability));
            });

          return capabilities;
        } catch (e) {
          return capabilities;
        }
      },
    ),
  )
  public desiredCapabilities: Capability[];
  @serializable
  public machine: number;
  @serializable
  public gridUrl: string;
  @serializable
  public appiumURL: string;
  @serializable
  public appUrl: string;
  @serializable
  public deviceId: number;
  public createdBy: User;
  @serializable
  public createdById: number;
  public platform: Platform;
  public osVersion: string;
  public deviceName: string;
  public externalLabAppId: string;
  public executionEnvironment: ExecutionEnvironment;
  public isAdhocConfig: boolean = false;
  @serializable(
    custom(
      () => SKIP,
      (v) => new User().deserialize(v),
    ),
  )
  public createdByUser: User;
  @serializable
  public isRealDevice: boolean = true;
  @serializable
  public isCameraImageInjectionEnabled: boolean = false;
  @serializable
  public isNetworkLogsEnabled: boolean = false;
  @serializable
  public isTouchIdEnrolled: boolean = false;
  @serializable
  public lastUsedConfig: boolean;

  @serializable
  public desktopAppPath: string;

  deserialize(input: any): this {
    return Object.assign(
      this,
      deserialize(MobileInspectionSavedConfiguration, input),
    );
  }

  deserializeMobileConfigForm(input, setUploadVersion?): this {
    const convert: JSON = JSON.parse('{}');
    convert['executionLabType'] = input['executionLabType'];
    convert['browser'] = input['browser'];
    convert['platformOsVersionId'] = input['platformOsVersionId'];
    convert['platformBrowserVersionId'] = input['platformBrowserVersionId'];
    convert['platformScreenResolutionId'] = input['platformScreenResolutionId'];
    convert['platformDeviceId'] = input['platformDeviceId'];
    convert['appPathType'] = input['appPathType'];
    convert['appUploadId'] = input['appUploadId'];
    if (Boolean(setUploadVersion)) {
      convert['appUploadVersionId'] = input['appUploadVersionId'];
    }
    convert['browserStackAppId'] = input['browserStackAppId'];
    convert['sauceLabAppId'] = input['sauceLabAppId'];
    convert['androidAppPackage'] = input['androidAppPackage'];
    convert['androidAppActivity'] = input['androidAppActivity'];
    convert['iosBundleId'] = input['iosBundleId'];
    convert['pageTimeOut'] = input['pageTimeOut'];
    convert['stepTimeOut'] = input['stepTimeOut'];
    convert['enhanceTimeOut'] = input['enhanceTimeOut'];
    convert['captureScreenshots'] = input['screenshot'];
    convert['screenshotMode'] = input['screenshotMode'];
    convert['desiredCapabilities'] = input['capabilities'];
    convert['machine'] = input['targetMachine'];
    convert['gridUrl'] = input['gridUrl'];
    convert['appiumURL'] = input['appiumURL'];
    convert['globalParameters'] = input['environmentId'];
    convert['appUrl'] = input['appUrl'];
    convert['deviceId'] = input['deviceId'];
    convert['isHeadless'] = input['isHeadless'];
    convert['isRealDevice'] = input['isRealDevice'];
    convert['isCameraImageInjectionEnabled'] =
      input['isCameraImageInjectionEnabled'];
    convert['isNetworkLogsEnabled'] = input['isNetworkLogsEnabled'];
    convert['isTouchIdEnrolled'] = input['isTouchIdEnrolled'];
    convert['desktopAppPath'] = input['desktopAppPath'];
    return Object.assign(
      this,
      deserialize(MobileInspectionSavedConfiguration, convert),
    );
  }

  equals(input: MobileInspectionSavedConfiguration) {
    let changed =
      this.executionLabType != input.executionLabType ||
      this.platformOsVersionId != input.platformOsVersionId ||
      this.platformDeviceId != input.platformDeviceId ||
      this.appPathType != input.appPathType ||
      this.appUploadId != input.appUploadId ||
      this.appUploadVersionId != input.appUploadVersionId ||
      this.androidAppPackage != input.androidAppPackage ||
      this.androidAppActivity != input.androidAppActivity ||
      this.iosBundleId != input.iosBundleId ||
      (Boolean(this.machine) && this.machine != input.machine) ||
      this.deviceId != input.deviceId ||
      this.isRealDevice != input.isRealDevice;
    this.desktopAppPath != input.desktopAppPath ||
      this.desiredCapabilities?.forEach((cap: Capability) => {
        if (!!cap.value && !!cap.name && !!cap.dataType)
          if (
            !input.desiredCapabilities?.find((envCap: Capability) => {
              envCap = new Capability().deserialize(envCap);
              return (
                envCap.dataType == cap.dataType &&
                envCap.name == cap.name &&
                envCap.value == cap.value
              );
            })
          )
            changed = true;
      });
    if (
      this.desiredCapabilities?.length == 0 &&
      input.desiredCapabilities?.length > 0
    ) {
      changed = true;
    }
    return !changed;
  }

  formattedName(environment): string {
    let name = environment.platform + ' (' + environment.osVersion + ') ';
    if (environment.deviceName != null) name += environment.deviceName;
    return name;
  }

  formattedHybridName(localHostName, environment): string {
    let name = localHostName + ' - ';
    if (environment?.name != null) name += environment.name;
    return name;
  }

  get isHybrid() {
    return this.executionLabType === ExecutionLabType.Hybrid;
  }

  get isTestSigmaLab(): boolean {
    return this.executionLabType == ExecutionLabType.TestsigmaLab;
  }

  get isIOSNative(): boolean {
    return this.applicationType == ApplicationType.IOSNative;
  }

  get isAndroidNative(): boolean {
    return this.applicationType == ApplicationType.AndroidNative;
  }

  get isSauceLab(): boolean {
    return this.executionLabType == ExecutionLabType.SauceLabs;
  }

  get isBrowserStack(): boolean {
    return this.executionLabType == ExecutionLabType.BrowserStack;
  }

  public serialize(): JSON {
    return serialize(this);
  }
}
